import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from './ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Badge } from './ui/badge';
import { Card, CardContent } from './ui/card';
import { Progress } from './ui/progress';
import { ScreeningQuestionStatus } from './ScreeningQuestionStatus';
import {
  Phone, ChevronLeft, ChevronRight, CheckCircle2, XCircle, AlertCircle,
  Star, Loader2, FileText, User, Building2, Send, Clock, MessageSquare
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const TelephonicScreeningDialog = ({ 
  isOpen, 
  onClose, 
  application, 
  getAuthToken,
  onComplete
}) => {
  const [loading, setLoading] = useState(true);
  const [screening, setScreening] = useState(null);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [responses, setResponses] = useState({});
  const [isSaving, setIsSaving] = useState(false);
  const [isCompleting, setIsCompleting] = useState(false);
  const [recruiterNotes, setRecruiterNotes] = useState('');
  const [recommendation, setRecommendation] = useState('');
  const [showSummary, setShowSummary] = useState(false);
  const [refreshingQuestions, setRefreshingQuestions] = useState(false);
  const [questionsError, setQuestionsError] = useState('');
  const loadVersion = useRef(0);

  const questionContext = () => ({
    job_id: application.job_id || null,
    position_name: application.job_title || application.position_name || application.process || '',
    company_name: application.company_name || null
  });
  const restoreScreening = record => {
    setScreening(record);
    setResponses(Object.fromEntries((record.responses || []).map(response => [response.question_id, response])));
    setRecruiterNotes(record.recruiter_notes || '');
    setRecommendation(record.recommendation || '');
  };
  const refreshRequest = (id, token) => axios.post(`${API}/telephonic-screenings/${id}/refresh-questions`, questionContext(), {
    headers: { Authorization: `Bearer ${token}` }
  });
  const refreshErrorText = error => typeof error.response?.data?.detail === 'string'
    ? error.response.data.detail : 'Could not check the question bank. Please try Refresh questions again.';

  // Initialize screening session
  useEffect(() => {
    if (isOpen && application) {
      startScreening(++loadVersion.current);
    }
    return () => { loadVersion.current += 1; };
  }, [isOpen, application]);

  const startScreening = async version => {
    setLoading(true);
    setScreening(null);
    setResponses({});
    setQuestionsError('');
    setRefreshingQuestions(false);
    setCurrentQuestionIndex(0);
    setShowSummary(false);
    setRecruiterNotes('');
    setRecommendation('');
    try {
      const token = getAuthToken();
      
      // Check if there's an existing incomplete screening
      const existingRes = await axios.get(`${API}/telephonic-screenings`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { application_id: application.id, status: 'In Progress' }
      });
      
      if (existingRes.data.length > 0) {
        let existing = existingRes.data[0];
        // An empty snapshot may predate its bank. Existing questions and answers
        // remain frozen; only empty, unanswered sessions are re-matched.
        if (!existing.questions_snapshot?.length && !existing.responses?.length) {
          try { existing = (await refreshRequest(existing.id, token)).data; }
          catch (error) { if (version === loadVersion.current) setQuestionsError(refreshErrorText(error)); }
        }
        if (version === loadVersion.current) restoreScreening(existing);
      } else {
        // Start new screening
        const res = await axios.post(`${API}/telephonic-screenings`, {
          application_id: application.id,
          ...questionContext(),
          candidate_name: application.candidate_name || application.applicant_name || application.full_name || '',
          candidate_email: application.candidate_email || application.email,
          candidate_phone: application.candidate_phone || application.phone,
        }, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (version === loadVersion.current) restoreScreening(res.data);
      }
    } catch (error) {
      console.error('Failed to start screening:', error);
      if (version === loadVersion.current) { toast.error('Failed to load screening questions'); onClose(); }
    } finally {
      if (version === loadVersion.current) setLoading(false);
    }
  };

  const refreshQuestions = async () => {
    if (refreshingQuestions || !screening || screening.questions_snapshot?.length || Object.keys(responses).length) return;
    const version = loadVersion.current;
    setRefreshingQuestions(true);
    setQuestionsError('');
    try {
      const result = await refreshRequest(screening.id, getAuthToken());
      if (version === loadVersion.current) { restoreScreening(result.data); setCurrentQuestionIndex(0); }
    } catch (error) {
      if (version === loadVersion.current) setQuestionsError(refreshErrorText(error));
    } finally {
      if (version === loadVersion.current) setRefreshingQuestions(false);
    }
  };

  const questions = screening?.questions_snapshot || [];
  const currentQuestion = questions[currentQuestionIndex];
  const progress = questions.length > 0 ? ((currentQuestionIndex + 1) / questions.length) * 100 : 0;

  // Update response for current question
  const updateResponse = (field, value) => {
    const questionId = currentQuestion.id;
    setResponses(prev => ({
      ...prev,
      [questionId]: {
        ...prev[questionId],
        question_id: questionId,
        question_text: currentQuestion.question_text,
        question_type: currentQuestion.question_type,
        [field]: value
      }
    }));
  };

  // Calculate score based on response
  const calculateScore = (response, question) => {
    if (!response) return 0;
    const weightage = question.weightage || 5;
    
    switch (question.question_type) {
      case 'rating':
        return (response.rating || 0) * weightage / 10;
      case 'scale':
        return ((response.rating || 0) / 5) * weightage * 10;
      case 'yes_no':
        return response.response === 'yes' ? weightage * 10 : 0;
      case 'multiple_choice':
        const selectedOpts = response.selected_options || [];
        const correctOpts = question.options?.filter(o => o.is_correct).map(o => o.id) || [];
        const isCorrect = selectedOpts.some(s => correctOpts.includes(s));
        return isCorrect ? weightage * 10 : 0;
      default:
        return (response.rating || 5) * weightage;
    }
  };

  // Save progress
  const saveProgress = async () => {
    if (!screening) return;
    
    setIsSaving(true);
    try {
      const token = getAuthToken();
      
      const responsesArray = Object.values(responses).map(r => ({
        ...r,
        score: calculateScore(r, questions.find(q => q.id === r.question_id))
      }));
      
      await axios.patch(`${API}/telephonic-screenings/${screening.id}`, {
        responses: responsesArray,
        recruiter_notes: recruiterNotes
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Progress saved');
    } catch (error) {
      toast.error('Failed to save progress');
    } finally {
      setIsSaving(false);
    }
  };

  // Complete screening
  const completeScreening = async () => {
    if (!recommendation) {
      toast.error('Please select a recommendation');
      return;
    }
    
    setIsCompleting(true);
    try {
      const token = getAuthToken();
      
      const responsesArray = Object.values(responses).map(r => ({
        ...r,
        score: calculateScore(r, questions.find(q => q.id === r.question_id))
      }));
      
      const res = await axios.post(`${API}/telephonic-screenings/${screening.id}/complete`, {
        responses: responsesArray,
        recruiter_notes: recruiterNotes,
        recommendation: recommendation
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setScreening(res.data);
      setShowSummary(true);
      toast.success('Screening completed!');
      
      if (onComplete) {
        onComplete(res.data);
      }
    } catch (error) {
      toast.error('Failed to complete screening');
    } finally {
      setIsCompleting(false);
    }
  };

  // Render question input based on type
  const renderQuestionInput = () => {
    if (!currentQuestion) return null;
    
    const response = responses[currentQuestion.id] || {};
    
    switch (currentQuestion.question_type) {
      case 'text':
        return (
          <div className="space-y-3">
            <Textarea
              data-testid="screening-answer-text"
              value={response.response || ''}
              onChange={(e) => updateResponse('response', e.target.value)}
              placeholder="Record candidate's answer..."
              rows={4}
              className="text-base"
            />
            {currentQuestion.expected_answer && (
              <div className="p-3 bg-blue-50 rounded-lg border border-blue-100">
                <p className="text-xs font-medium text-blue-700 mb-1">Expected Answer:</p>
                <p className="text-sm text-blue-600">{currentQuestion.expected_answer}</p>
              </div>
            )}
            <div className="space-y-2">
              <Label className="text-sm">Rate this answer (1-10)</Label>
              <div className="flex flex-wrap gap-1">
                {[1,2,3,4,5,6,7,8,9,10].map(n => (
                  <button
                    key={n}
                    onClick={() => updateResponse('rating', n)}
                    data-testid={`screening-text-rating-${n}`}
                    className={`w-9 h-9 rounded-lg text-sm font-medium transition-colors ${
                      response.rating === n 
                        ? 'bg-violet-600 text-white' 
                        : 'bg-slate-100 hover:bg-slate-200 text-slate-700'
                    }`}
                  >
                    {n}
                  </button>
                ))}
              </div>
            </div>
          </div>
        );
      
      case 'multiple_choice':
        return (
          <div className="space-y-3">
            {currentQuestion.options?.map((option, idx) => (
              <button
                key={option.id || idx}
                onClick={() => {
                  const current = response.selected_options || [];
                  const newSelected = current.includes(option.id || String(idx))
                    ? current.filter(s => s !== (option.id || String(idx)))
                    : [...current, option.id || String(idx)];
                  updateResponse('selected_options', newSelected);
                }}
                className={`w-full p-4 rounded-lg border-2 text-left transition-all ${
                  (response.selected_options || []).includes(option.id || String(idx))
                    ? 'border-violet-500 bg-violet-50'
                    : 'border-slate-200 hover:border-violet-300'
                }`}
              >
                <div className="flex items-center gap-3">
                  <span className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                    (response.selected_options || []).includes(option.id || String(idx))
                      ? 'bg-violet-500 text-white'
                      : 'bg-slate-100 text-slate-600'
                  }`}>
                    {String.fromCharCode(65 + idx)}
                  </span>
                  <span className="flex-1">{option.text}</span>
                  {option.is_correct && (response.selected_options || []).includes(option.id || String(idx)) && (
                    <CheckCircle2 className="h-5 w-5 text-green-500" />
                  )}
                </div>
              </button>
            ))}
          </div>
        );
      
      case 'rating':
        return (
          <div className="space-y-4">
            <div className="flex justify-center gap-2">
              {[1,2,3,4,5,6,7,8,9,10].map(n => (
                <button
                  key={n}
                  onClick={() => updateResponse('rating', n)}
                  className={`w-12 h-12 rounded-xl text-lg font-semibold transition-all ${
                    response.rating === n 
                      ? 'bg-gradient-to-br from-violet-500 to-purple-600 text-white scale-110 shadow-lg' 
                      : response.rating > n 
                        ? 'bg-violet-100 text-violet-600' 
                        : 'bg-slate-100 hover:bg-slate-200 text-slate-600'
                  }`}
                >
                  {n}
                </button>
              ))}
            </div>
            <div className="flex justify-between text-sm text-slate-500 px-2">
              <span>Poor</span>
              <span>Excellent</span>
            </div>
          </div>
        );
      
      case 'yes_no':
        return (
          <div className="flex gap-4 justify-center">
            <button
              onClick={() => updateResponse('response', 'yes')}
              className={`flex-1 max-w-[200px] p-6 rounded-xl border-2 transition-all ${
                response.response === 'yes'
                  ? 'border-green-500 bg-green-50'
                  : 'border-slate-200 hover:border-green-300'
              }`}
            >
              <CheckCircle2 className={`h-12 w-12 mx-auto mb-2 ${
                response.response === 'yes' ? 'text-green-500' : 'text-slate-300'
              }`} />
              <span className="text-lg font-medium">Yes</span>
            </button>
            <button
              onClick={() => updateResponse('response', 'no')}
              className={`flex-1 max-w-[200px] p-6 rounded-xl border-2 transition-all ${
                response.response === 'no'
                  ? 'border-red-500 bg-red-50'
                  : 'border-slate-200 hover:border-red-300'
              }`}
            >
              <XCircle className={`h-12 w-12 mx-auto mb-2 ${
                response.response === 'no' ? 'text-red-500' : 'text-slate-300'
              }`} />
              <span className="text-lg font-medium">No</span>
            </button>
          </div>
        );
      
      case 'scale':
        return (
          <div className="space-y-4">
            <div className="flex justify-center gap-3">
              {[1,2,3,4,5].map(n => (
                <button
                  key={n}
                  onClick={() => updateResponse('rating', n)}
                  className={`w-16 h-16 rounded-xl transition-all ${
                    response.rating === n 
                      ? 'bg-gradient-to-br from-amber-400 to-orange-500 text-white scale-110 shadow-lg' 
                      : 'bg-slate-100 hover:bg-slate-200'
                  }`}
                >
                  <Star className={`h-8 w-8 mx-auto ${
                    response.rating >= n ? 'fill-current' : ''
                  }`} />
                </button>
              ))}
            </div>
            <div className="flex justify-between text-sm text-slate-500 px-4">
              <span>1 - Poor</span>
              <span>5 - Excellent</span>
            </div>
          </div>
        );
      
      default:
        return (
          <Textarea
            value={response.response || ''}
            onChange={(e) => updateResponse('response', e.target.value)}
            placeholder="Record answer..."
            rows={4}
          />
        );
    }
  };

  // Render summary view
  const renderSummary = () => {
    const totalScore = Object.values(responses).reduce((sum, r) => {
      const q = questions.find(q => q.id === r.question_id);
      return sum + calculateScore(r, q);
    }, 0);
    
    const maxScore = screening?.max_possible_score || 100;
    const percentage = maxScore > 0 ? Math.round((totalScore / maxScore) * 100) : 0;
    
    const getRatingColor = (pct) => {
      if (pct >= 80) return 'text-green-600 bg-green-100';
      if (pct >= 60) return 'text-blue-600 bg-blue-100';
      if (pct >= 40) return 'text-amber-600 bg-amber-100';
      return 'text-red-600 bg-red-100';
    };
    
    return (
      <div className="space-y-6">
        <div className="text-center">
          <div className={`inline-flex items-center gap-2 px-6 py-3 rounded-full text-2xl font-bold ${getRatingColor(percentage)}`}>
            <Star className="h-8 w-8 fill-current" />
            {percentage}%
          </div>
          <p className="text-slate-500 mt-2">{screening?.overall_rating || 'Completed'}</p>
        </div>
        
        <div className="grid grid-cols-3 gap-4 text-center">
          <Card>
            <CardContent className="pt-4">
              <p className="text-2xl font-bold text-slate-800">{totalScore}</p>
              <p className="text-xs text-slate-500">Total Score</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4">
              <p className="text-2xl font-bold text-slate-800">{maxScore}</p>
              <p className="text-xs text-slate-500">Max Score</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4">
              <p className="text-2xl font-bold text-slate-800">{questions.length}</p>
              <p className="text-xs text-slate-500">Questions</p>
            </CardContent>
          </Card>
        </div>
        
        <div className={`p-4 rounded-lg text-center ${
          recommendation === 'Proceed' ? 'bg-green-100 text-green-700' :
          recommendation === 'Hold' ? 'bg-amber-100 text-amber-700' :
          'bg-red-100 text-red-700'
        }`}>
          <p className="font-semibold text-lg">Recommendation: {recommendation}</p>
        </div>
        
        {recruiterNotes && (
          <div className="p-4 bg-slate-50 rounded-lg">
            <p className="text-sm font-medium text-slate-600 mb-1">Recruiter Notes:</p>
            <p className="text-slate-700">{recruiterNotes}</p>
          </div>
        )}
        
        <Button onClick={onClose} className="w-full">
          Close
        </Button>
      </div>
    );
  };

  if (loading) {
    return (
      <Dialog open={isOpen} onOpenChange={onClose}>
        <DialogContent data-testid="screening-loading-dialog" className="max-w-2xl">
          <DialogTitle className="sr-only">Telephonic Screening</DialogTitle>
          <DialogDescription className="sr-only">Loading candidate questions</DialogDescription>
          <div className="flex flex-col items-center justify-center py-12">
            <Loader2 className="h-12 w-12 animate-spin text-violet-500 mb-4" />
            <p className="text-slate-500">Loading screening questions...</p>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent data-testid="telephonic-screening-dialog" className="w-[calc(100%-2rem)] max-w-3xl max-h-[90vh] overflow-y-auto [overflow-wrap:anywhere]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Phone className="h-5 w-5 text-violet-500" />
            Telephonic Screening
          </DialogTitle>
          <DialogDescription>Candidate screening assessment</DialogDescription>
        </DialogHeader>

        {/* Candidate Info */}
        <div className="flex items-center gap-4 p-4 bg-gradient-to-r from-violet-50 to-purple-50 rounded-lg border border-violet-100">
          <div className="h-12 w-12 shrink-0 rounded-full bg-violet-200 flex items-center justify-center">
            <User className="h-6 w-6 text-violet-600" />
          </div>
          <div className="flex-1 min-w-0">
            <p data-testid="screening-candidate-name" className="font-semibold text-slate-800">
              {application?.candidate_name || application?.applicant_name}
            </p>
            <div className="flex flex-wrap gap-3 text-sm text-slate-500">
              <span className="flex items-center gap-1">
                <Building2 className="h-3 w-3" />
                {application?.job_title || application?.position_name}
              </span>
              {application?.candidate_phone && (
                <span className="flex items-center gap-1">
                  <Phone className="h-3 w-3" />
                  {application.candidate_phone}
                </span>
              )}
            </div>
          </div>
        </div>

        {showSummary ? (
          renderSummary()
        ) : questions.length === 0 ? (
          <ScreeningQuestionStatus screening={screening} busy={refreshingQuestions} error={questionsError} onRefresh={refreshQuestions} />
        ) : (
          <>
            {screening?.question_bank_name && <p data-testid="screening-matched-bank" className="text-xs text-slate-500 [overflow-wrap:anywhere]">
              Question bank: {screening.question_bank_name} · {questions.length} question{questions.length === 1 ? '' : 's'}
            </p>}
            {/* Progress */}
            <div className="space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-slate-500">Question {currentQuestionIndex + 1} of {questions.length}</span>
                <span className="font-medium text-violet-600">{Math.round(progress)}% Complete</span>
              </div>
              <Progress value={progress} className="h-2" />
            </div>

            {/* Current Question */}
            <div className="min-h-[300px]">
              <div className="mb-4">
                <div className="flex items-start gap-2">
                  <Badge variant="secondary" className="mt-1">Q{currentQuestionIndex + 1}</Badge>
                  <div>
                    <p data-testid="screening-question-text" className="text-lg font-medium text-slate-800">{currentQuestion?.question_text}</p>
                    {currentQuestion?.hint && (
                      <p className="text-sm text-slate-500 mt-1 italic">💡 {currentQuestion.hint}</p>
                    )}
                  </div>
                </div>
                <div className="flex gap-2 mt-2">
                  <Badge variant="outline" className="text-xs">
                    Weight: {currentQuestion?.weightage || 5}
                  </Badge>
                  {currentQuestion?.is_required && (
                    <Badge className="text-xs bg-red-100 text-red-600">Required</Badge>
                  )}
                </div>
              </div>

              {renderQuestionInput()}

              {/* Notes for this question */}
              <div className="mt-4 space-y-2">
                <Label className="text-sm flex items-center gap-1">
                  <MessageSquare className="h-3 w-3" />
                  Notes for this question
                </Label>
                <Textarea
                  value={responses[currentQuestion?.id]?.notes || ''}
                  onChange={(e) => updateResponse('notes', e.target.value)}
                  placeholder="Add any observations or notes..."
                  rows={2}
                  className="text-sm"
                />
              </div>
            </div>

            {/* Navigation */}
            <div className="flex flex-wrap gap-2 items-center justify-between pt-4 border-t">
              <Button
                variant="outline"
                onClick={() => setCurrentQuestionIndex(prev => Math.max(0, prev - 1))}
                data-testid="screening-previous-question"
                disabled={currentQuestionIndex === 0}
              >
                <ChevronLeft className="h-4 w-4 mr-1" />
                Previous
              </Button>
              
              <Button
                variant="ghost"
                onClick={saveProgress}
                data-testid="screening-save-progress"
                disabled={isSaving}
              >
                {isSaving ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Clock className="h-4 w-4 mr-1" />}
                Save Progress
              </Button>

              {currentQuestionIndex < questions.length - 1 ? (
                <Button
                  onClick={() => setCurrentQuestionIndex(prev => prev + 1)}
                  data-testid="screening-next-question"
                >
                  Next
                  <ChevronRight className="h-4 w-4 ml-1" />
                </Button>
              ) : (
                <Button
                  onClick={() => setShowSummary(true)}
                  className="bg-green-600 hover:bg-green-700"
                >
                  Review & Complete
                  <CheckCircle2 className="h-4 w-4 ml-1" />
                </Button>
              )}
            </div>

            {/* Final section - shown when reviewing */}
            {currentQuestionIndex === questions.length - 1 && (
              <div className="mt-6 pt-6 border-t space-y-4">
                <h3 className="font-semibold text-slate-800">Final Assessment</h3>
                
                <div className="space-y-2">
                  <Label>Overall Recruiter Notes</Label>
                  <Textarea
                    value={recruiterNotes}
                    onChange={(e) => setRecruiterNotes(e.target.value)}
                    placeholder="Summarize your overall impression of the candidate..."
                    rows={3}
                  />
                </div>
                
                <div className="space-y-2">
                  <Label>Recommendation *</Label>
                  <div className="flex gap-3">
                    {['Proceed', 'Hold', 'Reject'].map(rec => (
                      <button
                        key={rec}
                        onClick={() => setRecommendation(rec)}
                        className={`flex-1 p-4 rounded-lg border-2 transition-all ${
                          recommendation === rec
                            ? rec === 'Proceed' ? 'border-green-500 bg-green-50' :
                              rec === 'Hold' ? 'border-amber-500 bg-amber-50' :
                              'border-red-500 bg-red-50'
                            : 'border-slate-200 hover:border-slate-300'
                        }`}
                      >
                        <div className={`text-lg font-semibold ${
                          recommendation === rec
                            ? rec === 'Proceed' ? 'text-green-600' :
                              rec === 'Hold' ? 'text-amber-600' :
                              'text-red-600'
                            : 'text-slate-600'
                        }`}>
                          {rec}
                        </div>
                        <div className="text-xs text-slate-500 mt-1">
                          {rec === 'Proceed' ? 'Move to next round' :
                           rec === 'Hold' ? 'Need more review' :
                           'Not suitable'}
                        </div>
                      </button>
                    ))}
                  </div>
                </div>
                
                <Button
                  onClick={completeScreening}
                  data-testid="screening-complete"
                  disabled={isCompleting || !recommendation}
                  className="w-full bg-gradient-to-r from-violet-600 to-purple-600 hover:from-violet-700 hover:to-purple-700"
                >
                  {isCompleting ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Completing...
                    </>
                  ) : (
                    <>
                      <Send className="h-4 w-4 mr-2" />
                      Complete Screening
                    </>
                  )}
                </Button>
              </div>
            )}
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default TelephonicScreeningDialog;
